import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import { FileDropZone } from "@freightsimple/ui-core/Controls/FileDropZone";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";

/**
 * How many files one section of the claim will take. The carrier only ever
 * looks at a handful, and an unbounded list is how a claim upload turns into a
 * hundred-megabyte POST.
 */
const MAX_CLAIM_FILES = 10;

// Invoices, inspection reports and repair bills arrive as PDFs; damage evidence
// arrives as photos. Every section takes both, because customers routinely
// scan a document to an image and photograph an invoice.
const ACCEPTED_FILE_TYPES = ".pdf,.tiff,.tif,.jpg,.jpeg,.png";

interface UploadClaimDocumentsSectionProps {
  /** Set on the underlying file input — the E2E suite attaches files through it. */
  id: string;
  files: Array<File>;
  setFiles: (_: Array<File>) => void;
  forceValidation: boolean;
  errorMessage: ErrorMessageType;
  disabled?: boolean | undefined;
}

/**
 * One upload slot of the submit-claim form. The surrounding numbered section
 * already says what belongs here, so this renders no heading of its own — it is
 * the drop target, the file list, and the count.
 */
export function ClaimsUploadClaimDocumentsSection(
  props: UploadClaimDocumentsSectionProps,
) {
  const { files, setFiles } = props;
  const atLimit = files.length >= MAX_CLAIM_FILES;

  return (
    <ShowFormErrors show={props.forceValidation}>
      <FormItem
        error={props.errorMessage}
        hint={
          atLimit
            ? `Maximum ${MAX_CLAIM_FILES} files reached`
            : `Up to ${MAX_CLAIM_FILES} files (${files.length}/${MAX_CLAIM_FILES})`
        }
        disabled={props.disabled}
        fillWidth
      >
        <FileDropZone
          id={props.id}
          accept={ACCEPTED_FILE_TYPES}
          multiple
          value={files}
          // FileDropZone hands back the whole list, so the cap is applied here
          // rather than by refusing the drop — a customer who drags twelve
          // photos keeps the first ten instead of losing all twelve.
          onChange={(newFiles) => setFiles(newFiles.slice(0, MAX_CLAIM_FILES))}
          // Deliberately not disabled at the limit. FileDropZone puts
          // `pointer-events-none` on the container the file rows live in, so
          // disabling it would kill each row's remove button too — leaving a
          // customer who dropped ten photos with no way back under the cap.
          // The slice above already enforces it, and the hint says so.
          disabled={props.disabled ?? false}
        />
      </FormItem>
    </ShowFormErrors>
  );
}
